/**
 * WeeklyScheduleEditor — reusable weekly ALLOWED-windows editor (IST).
 *
 * Shared by the per-user Login Schedule (Item 3) and the global Business
 * Hours schedule (Item 4). A schedule is an object keyed by weekday short
 * name, each holding a list of { start:'HH:MM', end:'HH:MM' } windows. An
 * empty list for a day means CLOSED (no access that day).
 */
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Trash2, Clock } from 'lucide-react';

export const DAYS = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
];

export const EMPTY_SCHEDULE = {
  mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [],
};

// Item 3 default: Mon–Fri 09:30–19:00, Sat 09:30–17:00, Sun closed.
export const DEFAULT_BUSINESS_SCHEDULE = {
  mon: [{ start: '09:30', end: '19:00' }],
  tue: [{ start: '09:30', end: '19:00' }],
  wed: [{ start: '09:30', end: '19:00' }],
  thu: [{ start: '09:30', end: '19:00' }],
  fri: [{ start: '09:30', end: '19:00' }],
  sat: [{ start: '09:30', end: '17:00' }],
  sun: [],
};

export function normalizeSchedule(sched) {
  const out = {};
  DAYS.forEach(({ key }) => {
    const arr = Array.isArray(sched?.[key]) ? sched[key] : [];
    out[key] = arr.map((w) => ({ start: w.start || '09:30', end: w.end || '18:00' }));
  });
  return out;
}

export function WeeklyScheduleEditor({ value, onChange, disabled = false, testId = 'weekly-schedule' }) {
  const sched = normalizeSchedule(value);

  const update = (dayKey, windows) => {
    onChange({ ...sched, [dayKey]: windows });
  };
  const addWindow = (dayKey) => {
    update(dayKey, [...sched[dayKey], { start: '09:30', end: '18:00' }]);
  };
  const removeWindow = (dayKey, idx) => {
    update(dayKey, sched[dayKey].filter((_, i) => i !== idx));
  };
  const setField = (dayKey, idx, field, val) => {
    update(dayKey, sched[dayKey].map((w, i) => (i === idx ? { ...w, [field]: val } : w)));
  };

  return (
    <div className={`rounded-md border border-[var(--divider)] divide-y divide-[var(--divider)] ${disabled ? 'opacity-60 pointer-events-none' : ''}`} data-testid={testId}>
      {DAYS.map(({ key, label }) => {
        const windows = sched[key];
        const closed = windows.length === 0;
        return (
          <div key={key} className="flex items-start gap-3 px-3 py-2.5" data-testid={`${testId}-day-${key}`}>
            <div className="w-24 pt-1.5 shrink-0">
              <div className="text-sm font-medium text-[var(--ink-navy)]">{label}</div>
              {closed && <div className="text-[10px] uppercase tracking-wide text-rose-500 mt-0.5">Closed</div>}
            </div>
            <div className="flex-1 space-y-1.5">
              {windows.map((w, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`${testId}-${key}-window-${idx}`}>
                  <Input
                    type="time"
                    value={w.start}
                    onChange={(e) => setField(key, idx, 'start', e.target.value)}
                    className="h-8 w-[130px]"
                    data-testid={`${testId}-${key}-start-${idx}`}
                  />
                  <span className="text-slate-400 text-xs">to</span>
                  <Input
                    type="time"
                    value={w.end}
                    onChange={(e) => setField(key, idx, 'end', e.target.value)}
                    className="h-8 w-[130px]"
                    data-testid={`${testId}-${key}-end-${idx}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeWindow(key, idx)}
                    className="text-slate-400 hover:text-rose-600 p-1 rounded"
                    title="Remove window"
                    data-testid={`${testId}-${key}-remove-${idx}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-[var(--ink-navy)]"
                onClick={() => addWindow(key)}
                data-testid={`${testId}-${key}-add`}
              >
                <Plus size={12} className="mr-1" /> {closed ? 'Open this day' : 'Add window'}
              </Button>
            </div>
          </div>
        );
      })}
      <div className="px-3 py-2 bg-[var(--paper-warm)] text-[10px] text-slate-500 flex items-center gap-1.5">
        <Clock size={11} /> All times are Indian Standard Time (IST, GMT+5:30). An empty day = closed.
      </div>
    </div>
  );
}

export default WeeklyScheduleEditor;
